// API configuration with domain-based detection
// Automatically determines API URL based on the current domain

function getApiBaseUrl(): string {
  // First check if explicitly set via environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Auto-detect based on current hostname
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';

  // Production: admin.tripalta.com -> api.tripalta.com
  if (hostname === 'admin.tripalta.com') {
    return 'https://api.tripalta.com';
  }

  // Staging: stg.admin.tripalta.com -> api.stg.tripalta.com
  if (hostname === 'stg.admin.tripalta.com') {
    return 'https://api.stg.tripalta.com';
  }

  // Local development: admin.tripalta.local or localhost
  if (hostname === 'admin.tripalta.local' || hostname === 'localhost') {
    return 'http://localhost:8443';
  }

  // Fallback to localhost
  return 'http://localhost:8443';
}

function getSchedulerAdminApiBaseUrl(): string {
  if (import.meta.env.VITE_SCHEDULER_ADMIN_API_URL) {
    return import.meta.env.VITE_SCHEDULER_ADMIN_API_URL;
  }

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'admin.tripalta.com') {
    return 'https://api.tripalta.com';
  }

  if (hostname === 'stg.admin.tripalta.com') {
    return 'https://api.stg.tripalta.com';
  }

  if (hostname === 'admin.tripalta.local' || hostname === 'localhost') {
    return getApiBaseUrl();
  }

  return getApiBaseUrl();
}

export const API_BASE_URL = getApiBaseUrl();
export const SCHEDULER_ADMIN_API_BASE_URL = getSchedulerAdminApiBaseUrl();

// Export the environment for debugging
export const CURRENT_ENV = import.meta.env.VITE_ENV || 'development';
export const IS_PRODUCTION = CURRENT_ENV === 'production';
export const IS_STAGING = CURRENT_ENV === 'staging';
export const IS_DEVELOPMENT = CURRENT_ENV === 'development';

export const API_ENDPOINTS = {
  SETTINGS: {
    NOTIFICATION_PREFERENCES: '/api/v1/admin/settings/notification-preferences',
  },
  // Admin auth endpoints
  AUTH: {
    LOGOUT: '/api/v2/admin/auth/logout',
  },
  // Admin management endpoints
  USERS: {
    LIST: '/api/v1/admin/users',
    GET: (id: string) => `/api/v1/admin/users/${id}`,
    STATS: '/api/v1/admin/users/stats',
  },
  SUPPORT: {
    TICKETS: '/api/v1/admin/support/tickets',
    TICKET: (id: string) => `/api/v1/admin/support/tickets/${id}`,
    TICKET_STATUS: (id: string) => `/api/v1/admin/support/tickets/${id}/status`,
    TICKET_REPLY: (id: string) => `/api/v1/admin/support/tickets/${id}/reply`,
  },
  REFERRAL_INVITES: {
    LIST: '/api/v1/admin/referral-invites',
    GET: (id: string) => `/api/v1/admin/referral-invites/${id}`,
    CANCEL: (id: string) => `/api/v1/admin/referral-invites/${id}/cancel`,
    SUPPRESS: (id: string) => `/api/v1/admin/referral-invites/${id}/suppress`,
    PROVIDER_EVENT: '/api/v1/admin/referral-invites/provider-event',
  },
  REFERRAL_REWARDS: {
    PROGRAM: '/api/v1/admin/referrals/reward-program',
    LIST: '/api/v1/admin/referrals/rewards',
    BLOCK: (id: string) => `/api/v1/admin/referrals/rewards/${id}/block`,
    REVERSE: (id: string) => `/api/v1/admin/referrals/rewards/${id}/reverse`,
    RETRY: (id: string) => `/api/v1/admin/referrals/rewards/${id}/retry`,
    RELEASE_RESERVATION: (id: string) =>
      `/api/v1/admin/referrals/rewards/${id}/release-reservation`,
  },
  COLLABORATION: {
    COLLABORATORS: '/api/v1/admin/collaborators',
    COLLABORATOR: (id: string) => `/api/v1/admin/collaborators/${id}`,
    TRIP_INVITES: '/api/v1/admin/trip-invites',
    TRIP_INVITE: (id: string) => `/api/v1/admin/trip-invites/${id}`,
    TRIP_INVITE_RESEND: (id: string) =>
      `/api/v1/admin/trip-invites/${id}/resend`,
    SHARE_LINKS: '/api/v1/admin/share-links',
    SHARE_LINK: (id: string) => `/api/v1/admin/share-links/${id}`,
    REPORTS: '/api/v1/admin/reports',
    REPORT: (id: string) => `/api/v1/admin/reports/${id}`,
    INVITE_ABUSE_PATTERNS: '/api/v1/admin/abuse/invite-patterns',
    INVITE_ABUSE_BLOCK: '/api/v1/admin/abuse/block-user',
    INVITE_ABUSE_BLOCKS: '/api/v1/admin/abuse/blocked-users',
    BLOCKED_USER_CONFLICTS: '/api/v1/admin/abuse/blocked-user-conflicts',
    INVITE_ABUSE_UNBLOCK: (id: string) =>
      `/api/v1/admin/abuse/blocked-users/${id}/unblock`,
  },
  TRIPS: {
    LIST: '/api/v1/admin/trips',
    GET: (id: string) => `/api/v1/admin/trips/${id}`,
    STATS: '/api/v1/admin/trips/stats',
    ACTIVITY_TYPES: '/api/v1/admin/trips/activity-types',
    ACTIVITY_TYPE: (id: number) => `/api/v1/admin/trips/activity-types/${id}`,
    ACTIVITY_TYPE_ENABLE: (id: number) =>
      `/api/v1/admin/trips/activity-types/${id}/enable`,
    ACTIVITY_TYPE_DISABLE: (id: number) =>
      `/api/v1/admin/trips/activity-types/${id}/disable`,
    DESTINATIONS: '/api/v1/admin/trips/destinations/popular',
    DESTINATION: (id: string) =>
      `/api/v1/admin/trips/destinations/popular/${id}`,
    DESTINATIONS_REFRESH: '/api/v1/admin/trips/destinations/popular/refresh',
    CATALOG_SEARCH: '/api/v1/admin/trips/destinations/catalog',
    CATALOG_FEATURED: '/api/v1/admin/trips/destinations/catalog/featured',
    CATALOG_FEATURED_FLAG: (id: string) =>
      `/api/v1/admin/trips/destinations/catalog/${id}/featured`,
    CATALOG_FEATURED_ORDER:
      '/api/v1/admin/trips/destinations/catalog/featured/order',
    CATALOG_MEDIA: (id: string) =>
      `/api/v1/admin/trips/destinations/${id}/media`,
    CATALOG_MEDIA_PRESIGN: (id: string) =>
      `/api/v1/admin/trips/destinations/${id}/media/presign`,
    CATALOG_MEDIA_ITEM: (id: string, mediaId: string) =>
      `/api/v1/admin/trips/destinations/${id}/media/${mediaId}`,
    CATALOG_MEDIA_PRIMARY: (id: string, mediaId: string) =>
      `/api/v1/admin/trips/destinations/${id}/media/${mediaId}/primary`,
    AIRPORT_SEARCH: '/api/v1/airport-search',
  },
  BILLING: {
    // Products
    PRODUCTS: '/api/v1/admin/payments/products',
    PRODUCT: (id: string) => `/api/v1/admin/payments/products/${id}`,
    PRODUCT_ENABLE: (id: string) =>
      `/api/v1/admin/payments/products/${id}/enable`,
    PRODUCT_DISABLE: (id: string) =>
      `/api/v1/admin/payments/products/${id}/disable`,
    PRODUCT_PLANS: (id: string) =>
      `/api/v1/admin/payments/products/${id}/plans`,
    // Plans
    PLANS: '/api/v1/admin/payments/plans',
    PLAN: (id: string) => `/api/v1/admin/payments/plans/${id}`,
    PLAN_ENABLE: (id: string) => `/api/v1/admin/payments/plans/${id}/enable`,
    PLAN_DISABLE: (id: string) => `/api/v1/admin/payments/plans/${id}/disable`,
    // Features
    FEATURES: '/api/v1/admin/payments/features',
    FEATURE: (id: string) => `/api/v1/admin/payments/features/${id}`,
    FEATURE_ENABLE: (id: string) =>
      `/api/v1/admin/payments/features/${id}/enable`,
    FEATURE_DISABLE: (id: string) =>
      `/api/v1/admin/payments/features/${id}/disable`,
    // Entitlements
    ENTITLEMENTS: '/api/v1/admin/payments/entitlements',
    ENTITLEMENT: (id: string) => `/api/v1/admin/payments/entitlements/${id}`,
    PLAN_ENTITLEMENTS: (planId: string) =>
      `/api/v1/admin/payments/plans/${planId}/entitlements`,
    // Currencies
    CURRENCIES: '/api/v1/admin/payments/currencies',
    CURRENCY: (id: string) => `/api/v1/admin/payments/currencies/${id}`,
    CURRENCY_ENABLE: (id: string) =>
      `/api/v1/admin/payments/currencies/${id}/enable`,
    CURRENCY_DISABLE: (id: string) =>
      `/api/v1/admin/payments/currencies/${id}/disable`,
    // Exchange Rates
    EXCHANGE_RATES: '/api/v1/admin/payments/exchange-rates',
    EXCHANGE_RATE: (id: string) =>
      `/api/v1/admin/payments/exchange-rates/${id}`,
    EXCHANGE_RATES_BY_BASE: (base: string) =>
      `/api/v1/admin/payments/exchange-rates/base/${base}`,
    EXCHANGE_RATE_DEACTIVATE: (id: string) =>
      `/api/v1/admin/payments/exchange-rates/${id}/deactivate`,
    // Configuration
    CONFIGURATION: '/api/v1/admin/payments/configuration',
    // Analytics
    ANALYTICS: '/api/v1/admin/payments/analytics',
    // Subscriptions
    SUBSCRIPTION_LIST: '/api/v1/admin/payments/subscriptions',
    SUBSCRIPTION: (id: string) => `/api/v1/admin/payments/subscriptions/${id}`,
    SUBSCRIPTION_STATS: '/api/v1/admin/payments/subscriptions/stats',
    // Payments
    PAYMENTS: '/api/v1/admin/payments/payments',
    PAYMENT_STATS: '/api/v1/admin/payments/payments/stats',
    // User Entitlements
    USER_ENTITLEMENTS: '/api/v1/admin/payments/user-entitlements',
    USER_ENTITLEMENT: (id: string) =>
      `/api/v1/admin/payments/user-entitlements/${id}`,
    USER_ENTITLEMENTS_BY_USER: (userId: string) =>
      `/api/v1/admin/payments/user-entitlements/user/${userId}`,
  },
  BOOKINGS: {
    MARGINS: '/api/v1/admin/bookings/margins',
    MARGIN: (id: string) => `/api/v1/admin/bookings/margins/${id}`,
    MARGIN_ENABLE: (id: string) =>
      `/api/v1/admin/bookings/margins/${id}/enable`,
    MARGIN_DISABLE: (id: string) =>
      `/api/v1/admin/bookings/margins/${id}/disable`,
    PROVIDERS: '/api/v1/admin/bookings/providers',
    PROVIDER: (id: string) => `/api/v1/admin/bookings/providers/${id}`,
    PROVIDER_ENABLE: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/enable`,
    PROVIDER_DISABLE: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/disable`,
    PROVIDER_OPERATIONS_SUMMARY: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/operations-summary`,
    FLIGHT_STATUS_OPERATIONS_SUMMARY:
      '/api/v1/admin/bookings/flight-status/operations-summary',
    FLIGHT_WEBHOOK_REGISTRATIONS:
      '/api/v1/admin/bookings/flight-status/registrations',
    FLIGHT_WEBHOOK_INBOX: '/api/v1/admin/bookings/flight-status/webhook-inbox',
    PROVIDER_THROTTLING_PROFILE: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/throttling-profile`,
    PROVIDER_THROTTLING_OVERRIDE: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/throttling-override`,
    PROVIDER_AUDIT: (id: string) =>
      `/api/v1/admin/bookings/providers/${id}/audit`,
    ESIM_NOTIFICATIONS_SUBSCRIPTIONS:
      '/api/v1/admin/bookings/esim/notifications/subscriptions',
    ESIM_BALANCE: '/api/v1/admin/bookings/esim/balance',
    ESIM_WEBHOOK_SIMULATOR: '/api/v1/admin/bookings/esim/webhooks/simulator',
    ESIM_ORDERS: '/api/v1/admin/bookings/esim/orders',
    ESIM_ORDER: (id: string) => `/api/v1/admin/bookings/esim/orders/${id}`,
    ESIM_SIMS: '/api/v1/admin/bookings/esim/sims',
    ESIM_SIM_BRAND: (iccid: string) =>
      `/api/v1/admin/bookings/esim/sims/${iccid}/brand`,
    ESIM_REFUNDS: '/api/v1/admin/bookings/esim/refunds',
    ESIM_REFUND_RECONCILE: (id: string) =>
      `/api/v1/admin/bookings/esim/refunds/${id}/reconcile`,
    ESIM_VOUCHERS: '/api/v1/admin/bookings/esim/vouchers',
    ESIM_SYNC: '/api/v1/admin/bookings/esim/sync',
    ESIM_WEBHOOK_DLQ: '/api/v1/admin/bookings/webhooks/dlq',
    ESIM_WEBHOOK_DLQ_REPLAY: (id: string) =>
      '/api/v1/admin/bookings/webhooks/dlq/' + id + '/replay',
    VIATOR_OPERATIONS_SUMMARY:
      '/api/v1/admin/bookings/viator/operations-summary',
    VIATOR_EVIDENCE_EXPORT: '/api/v1/admin/bookings/viator/evidence-export',
    VIATOR_BOOKINGS: '/api/v1/admin/bookings/viator/bookings',
    VIATOR_BOOKING_CONFIRM: (ref: string) =>
      `/api/v1/admin/bookings/viator/bookings/${ref}/confirm`,
    VIATOR_BOOKING_REJECT: (ref: string) =>
      `/api/v1/admin/bookings/viator/bookings/${ref}/reject`,
    VIATOR_BOOKING_REFUND: (ref: string) =>
      `/api/v1/admin/bookings/viator/bookings/${ref}/refund`,
    VIATOR_BOOKING_RECONCILE: (ref: string) =>
      `/api/v1/admin/bookings/viator/bookings/${ref}/reconcile`,
    VIATOR_PROVIDER_HEALTH: '/api/v1/admin/bookings/viator/provider-health',
    VIATOR_AUDIT_LOG: '/api/v1/admin/bookings/viator/audit-log',
    VIATOR_SYNC_PRODUCTS: '/api/v1/bookings/viator/admin/sync/products',
    VIATOR_SYNC_AVAILABILITY: '/api/v1/bookings/viator/admin/sync/availability',
    VIATOR_SYNC_TAGS: '/api/v1/bookings/viator/admin/sync/tags',
    VIATOR_SYNC_BOOKING_QUESTIONS:
      '/api/v1/bookings/viator/admin/sync/booking-questions',
    VIATOR_SYNC_CANCEL_REASONS:
      '/api/v1/bookings/viator/admin/sync/cancel-reasons',
    VIATOR_SYNC_DESTINATIONS: '/api/v1/bookings/viator/admin/sync/destinations',
    VIATOR_SYNC_FULL_INGEST: '/api/v1/bookings/viator/admin/sync/full-ingest',
    VIATOR_SYNC_EXCHANGE_RATES:
      '/api/v1/bookings/viator/admin/sync/exchange-rates',
    VIATOR_SYNC_LOCATIONS: '/api/v1/bookings/viator/admin/sync/locations',
    VIATOR_SYNC_ATTRACTIONS: '/api/v1/bookings/viator/admin/sync/attractions',
    VIATOR_REPAIR_PRODUCTS_BULK:
      '/api/v1/bookings/viator/admin/repair/products-bulk',
    VIATOR_REPAIR_AVAILABILITY_BULK:
      '/api/v1/bookings/viator/admin/repair/availability-bulk',
    VIATOR_REPLAY_RAW_PAYLOADS:
      '/api/v1/bookings/viator/admin/replay/raw-payloads',
    VIATOR_PII_PURGE: '/api/v1/bookings/viator/admin/pii-purge',
    VIATOR_BOOKING_REFUND_FOLLOWUP: (ref: string) =>
      `/api/v1/admin/bookings/viator/bookings/${ref}/refund-followup`,
    REVENUE_SUMMARY: '/api/v1/admin/bookings/revenue/summary',
    REVENUE_LIST: '/api/v1/admin/bookings/revenue/list',
  },
  RESOLUTION: {
    REPORT: '/api/v1/admin/resolution/report',
    AUTO_CREATED_DESTINATIONS:
      '/api/v1/admin/resolution/auto-created-destinations',
    CANDIDATES_REVIEW: '/api/v1/admin/resolution/candidates/review',
    CANDIDATE: (id: string) => `/api/v1/admin/resolution/candidates/${id}`,
    PLACE_CANDIDATES: '/api/v1/admin/resolution/place-candidates',
    PLACE_CANDIDATE: (id: string) =>
      `/api/v1/admin/resolution/place-candidates/${id}`,
    PLACE_CANDIDATE_DRY_RUN: (id: string) =>
      `/api/v1/admin/resolution/place-candidates/${id}/dry-run`,
    PLACE_CANDIDATE_DECISION: (id: string) =>
      `/api/v1/admin/resolution/place-candidates/${id}/decision`,
    CANDIDATE_BACKFILL_DRY_RUN:
      '/api/v1/admin/resolution/candidates/backfill/dry-run',
    MEDIA_BACKFILL_DRY_RUN: '/api/v1/admin/resolution/media/backfill/dry-run',
  },
  ACTIVITY_MATCHES: {
    REPORT: '/api/v1/admin/activity-matches/report',
    REVIEW_CANDIDATES: '/api/v1/admin/activity-matches/review-candidates',
    STATUS: (id: string) => `/api/v1/admin/activity-matches/${id}/status`,
  },
  AI: {
    QUESTIONS: '/api/v1/admin/ai/questions',
    QUESTION: (id: string) => `/api/v1/admin/ai/questions/${id}`,
    QUESTION_ENABLE: (id: string) => `/api/v1/admin/ai/questions/${id}/enable`,
    QUESTION_DISABLE: (id: string) =>
      `/api/v1/admin/ai/questions/${id}/disable`,
    QUESTIONS_REORDER: '/api/v1/admin/ai/questions/reorder',
  },
  AUDIT: {
    // Admin audit feature moved from payments-service to user-service on 2026-04-21.
    // api-gateway now routes /api/v1/admin/audit/** to user-service; see
    // ta-repo/.plans/z1.md for context.
    LOGS: '/api/v1/admin/audit/logs',
  },
  SCHEDULER: {
    JOBS: '/api/v1/admin/scheduler/jobs',
    JOB_DETAIL: (name: string) => `/api/v1/admin/scheduler/jobs/${name}`,
    TRIGGER: (name: string) => `/api/v1/admin/scheduler/jobs/${name}/trigger`,
    PAUSE: (name: string) => `/api/v1/admin/scheduler/jobs/${name}/pause`,
    RESUME: (name: string) => `/api/v1/admin/scheduler/jobs/${name}/resume`,
    STATS: '/api/v1/admin/scheduler/stats',
  },
  BUDGETS: {
    LIST: '/api/v1/admin/budgets',
    ITEM: (id: string) => `/api/v1/admin/budgets/${id}`,
  },
  PROVIDER_VENDORS: {
    LIST: '/api/v1/admin/provider-vendors',
    ITEM: (id: string) => `/api/v1/admin/provider-vendors/${id}`,
  },
  NOTIFICATIONS: {
    TEMPLATES: '/api/v1/admin/notifications/templates',
    TEMPLATE: (id: string) => `/api/v1/admin/notifications/templates/${id}`,
    TEMPLATE_STATS: '/api/v1/admin/notifications/templates/stats',
    TEMPLATE_ENABLE: (id: string) =>
      `/api/v1/admin/notifications/templates/${id}/enable`,
    TEMPLATE_DISABLE: (id: string) =>
      `/api/v1/admin/notifications/templates/${id}/disable`,
    TEMPLATE_DUPLICATE: (id: string) =>
      `/api/v1/admin/notifications/templates/${id}/duplicate`,
    TEMPLATE_PREVIEW: (id: string) =>
      `/api/v1/admin/notifications/templates/${id}/preview`,
  },
} as const;
